import {
  Chatbot,
  currentAccountIdAtom,
  Interface,
  nodeAtomFamily,
  Page,
  sessionAtom,
  canvasAtom,
  Table,
  DefaultNodeData,
  takeSnapshotSetter,
} from "@zapier/canvas-state";
import { Button, Field, Text } from "@zapier/design-system";
import { useAtomValue, useSetAtom } from "jotai";
import { Node } from "@xyflow/react";
import { Suspense, useCallback } from "react";
import { useEmitResourceConnectEvents } from "@zapier/observability";

import { useEmitUserInteractionEvents } from "../../../hooks/useEmitUserInteractionEvents";
import { ErrorBoundary } from "../../ErrorBoundary";
import { TablesTypeahead } from "../../TablesTypeahead";
import { CreateNewTableButton } from "../../CreateNewTableButton";
import { SpinnerButton } from "../../SpinnerButton";
import { getInterfacesOrigin, getTablesOrigin } from "../../../utils/urls";
import { InterfacesTypeahead } from "../../InterfacesTypeahead";
import { CreateNewChatbotButton } from "../../CreateNewChatbotButton";
import { ChatbotsTypeahead } from "../../ChatbotsTypeahead";
import { PagesTypeahead } from "../../PagesTypeahead";
import { CreateNewInterfaceButton } from "../../CreateNewInterfaceButton";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { ASSET_APP_IDS } from "@zapier/canvas-constants";

import styles from "./AssetSelector.module.css";

type Props = {
  nodeId: string;
};

export function AssetSelector({ nodeId }: Props) {
  const node = useAtomValue(nodeAtomFamily(nodeId)) as Node<DefaultNodeData>;
  const appId = node.data.services?.[0]?.currentImplementation.id ?? "";
  const isTablesNode = appId.includes(ASSET_APP_IDS.TABLES);
  const isInterfacesNode = appId.includes(ASSET_APP_IDS.INTERFACES);
  const isChatbotsNode = appId.includes(ASSET_APP_IDS.CHATBOTS);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const session = useAtomValue(sessionAtom);
  const canvas = useAtomValue(canvasAtom);
  const currentAccountId = useAtomValue(currentAccountIdAtom);
  const updateNodeAndSave = useUpdateNode();
  const {
    emitEventOnTableConnect,
    emitEventOnInterfaceConnect,
    emitEventOnChatbotConnect,
  } = useEmitResourceConnectEvents({
    sessionId: session?.id,
    canvasId: canvas.id,
    currentAccountId: currentAccountId,
  });
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();

  const onChangeTable = useCallback(
    (table: Table | null | undefined) => {
      updateNodeAndSave({
        id: nodeId,
        data: {
          tableId: table?.id,
        },
      });

      if (table?.id) {
        emitEventOnTableConnect(table.id);
      }
    },
    [emitEventOnTableConnect, updateNodeAndSave, nodeId],
  );

  const onChangeProject = useCallback(
    (project: Interface) => {
      takeSnapshot();
      updateNodeAndSave({
        id: nodeId,
        data: {
          projectId: project?.id,
          pageId: undefined, // Clear out page id when project changes
        },
      });

      if (project?.id) {
        emitEventOnInterfaceConnect(project.id);
      }
    },
    [emitEventOnInterfaceConnect, updateNodeAndSave, takeSnapshot, nodeId],
  );

  const onChangePage = useCallback(
    (page: Page | null | undefined) => {
      takeSnapshot();
      updateNodeAndSave({
        id: nodeId,
        data: {
          pageId: page?.id,
        },
      });
    },
    [updateNodeAndSave, takeSnapshot, nodeId],
  );

  const onChangeChatbot = useCallback(
    (chatbot: Chatbot | null | undefined) => {
      takeSnapshot();
      updateNodeAndSave({
        id: nodeId,
        data: {
          chatbotId: chatbot?.id,
        },
      });

      if (chatbot?.id) {
        emitEventOnChatbotConnect(chatbot.id);
      }
    },
    [updateNodeAndSave, takeSnapshot, emitEventOnChatbotConnect, nodeId],
  );

  return (
    <>
      {isTablesNode && (
        <>
          <Field
            label="Table"
            renderInput={() => (
              <TablesTypeahead
                onChange={onChangeTable}
                selectedId={node.data.tableId}
              />
            )}
          />
          {!node.data.tableId && (
            <>
              <div className={styles.separator}>
                <Text type="SmallPrint1" color="GrayWarm6">
                  OR
                </Text>
              </div>
              <ErrorBoundary>
                <Suspense fallback={<SpinnerButton />}>
                  <CreateNewTableButton nodeId={nodeId} />
                </Suspense>
              </ErrorBoundary>
            </>
          )}
        </>
      )}
      {isInterfacesNode && (
        <>
          <Field
            label="Interface"
            renderInput={() => (
              <InterfacesTypeahead
                onChange={(item: any) => onChangeProject(item)}
                selectedId={node.data.projectId}
              />
            )}
          />
          {!node.data.projectId && (
            <>
              <div className={styles.separator}>
                <Text type="SmallPrint1" color="GrayWarm6">
                  OR
                </Text>
              </div>
              <ErrorBoundary>
                <Suspense fallback={<SpinnerButton />}>
                  <CreateNewInterfaceButton nodeId={nodeId} />
                </Suspense>
              </ErrorBoundary>
            </>
          )}
        </>
      )}
      {isInterfacesNode && node.data.projectId && (
        <Field
          label="Page"
          renderInput={() => (
            <PagesTypeahead
              onChange={onChangePage}
              selectedId={node.data.pageId}
              projectId={node.data.projectId as string}
            />
          )}
        />
      )}
      {isChatbotsNode && (
        <>
          <Field
            label="Chatbot"
            renderInput={() => (
              <ChatbotsTypeahead
                onChange={onChangeChatbot}
                selectedId={node.data.chatbotId}
              />
            )}
          />
          {!node.data.chatbotId && (
            <>
              <div className={styles.separator}>
                <Text type="SmallPrint1" color="GrayWarm6">
                  OR
                </Text>
              </div>
              <ErrorBoundary>
                <Suspense fallback={<SpinnerButton />}>
                  <CreateNewChatbotButton nodeId={nodeId} />
                </Suspense>
              </ErrorBoundary>
            </>
          )}
        </>
      )}
      {isTablesNode && node.data.tableId && (
        <Button
          color="primary"
          iconBefore="arrowOffsite"
          href={`//${getTablesOrigin()}/app/tables/t/${node.data.tableId}`}
          target="_blank"
          onClick={() =>
            emitEventOnAssetOpen({
              asset: "table",
              location: "sidebar",
            })
          }
        >
          Edit Table
        </Button>
      )}
      {isInterfacesNode && node.data.projectId && (
        <Button
          color="primary"
          iconBefore="arrowOffsite"
          href={`//${getInterfacesOrigin()}/interfaces/project/${
            node.data.projectId
          }`}
          target="_blank"
          onClick={() =>
            emitEventOnAssetOpen({
              asset: "interface",
              location: "sidebar",
            })
          }
        >
          Edit Interface
        </Button>
      )}
      {isChatbotsNode && node.data.chatbotId && (
        <Button
          color="primary"
          iconBefore="arrowOffsite"
          href={`/app/chatbots/${node.data.chatbotId}`}
          target="_blank"
          onClick={() =>
            emitEventOnAssetOpen({
              asset: "chatbot",
              location: "sidebar",
            })
          }
        >
          Edit Chatbot
        </Button>
      )}
    </>
  );
}
